import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
	ArrowLeft,
	ChevronLeft,
	ChevronRight,
	Eye,
	Download,
	ZoomIn,
	Calendar,
	User,
	Tag,
	DollarSign,
	Ruler,
	Palette,
	Shirt,
	ShoppingBag,
	AlertTriangle,
	CheckCircle,
	Clock,
	Image as ImageIcon,
	Paperclip,
	RefreshCw,
	Loader,
	Edit,
	Trash2,
	Share,
	X,
	FileText,
	Star,
	Truck,
	MessageSquare
} from 'lucide-react';
import { listingsService, dressStatusService, branchService, purchaseService, showToast } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import OffersSection from '../../components/OffersSection';
import { isSuperAdmin, isAuthenticator, isStoreManager } from '../../utils/rolePermissions';

const ListingDetails = () => {
	const { listingId } = useParams();
	const navigate = useNavigate();
	const { user } = useAuth();
	const [listing, setListing] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [currentImageArray, setCurrentImageArray] = useState([]);
	const [fullScreenImage, setFullScreenImage] = useState(null);
	const [dressStatuses, setDressStatuses] = useState([]);
	const [showSendToListingModal, setShowSendToListingModal] = useState(false);
	const [sendingToListing, setSendingToListing] = useState(false);
	const [branches, setBranches] = useState([]);
	const [branchesLoading, setBranchesLoading] = useState(false);
	const [selectedBranchId, setSelectedBranchId] = useState(null);
	const [showDelistModal, setShowDelistModal] = useState(false);
	const [delisting, setDelisting] = useState(false);
	const [submittingAuthentication, setSubmittingAuthentication] = useState(false);
	const [togglingFeatured, setTogglingFeatured] = useState(false);
	const [showReturnToSellerModal, setShowReturnToSellerModal] = useState(false);
	const [returnToSellerForm, setReturnToSellerForm] = useState({
		awbNumber: '',
		courierName: ''
	});
	const [returnSubmitting, setReturnSubmitting] = useState(false);
	
	// Edit listing content state
	const [editingContent, setEditingContent] = useState(false);
	const [listingContent, setListingContent] = useState('');
	const [savingContent, setSavingContent] = useState(false);
	const [showFullDescription, setShowFullDescription] = useState(false);

	// Image redaction state
	const [isRedactionMode, setIsRedactionMode] = useState(false);
	const [redactionAreas, setRedactionAreas] = useState({}); // Store by image ID
	const [currentRedaction, setCurrentRedaction] = useState(null);
	const [isDrawing, setIsDrawing] = useState(false);
	const [redactedImages, setRedactedImages] = useState(new Set()); // Track which images have been permanently redacted
	const [showOriginal, setShowOriginal] = useState(false); // Toggle between redacted and original image

	// Authentication parameters for authenticator role
	const [authParams, setAuthParams] = useState({
		purchaseProof: 0,
		fabric: 0,
		stitching: 0,
		labels: 0,
		condition: 0
	});
	const [overallRemark, setOverallRemark] = useState('');
	const [overallEstimate, setOverallEstimate] = useState('');

	// Helper function to safely render values (handles objects with name property)
	const renderValue = (value) => {
		if (!value) return 'N/A';
		if (typeof value === 'object' && value !== null) {
			return value.name || value.id || JSON.stringify(value);
		}
		return value;
	};

	// Get status icon
	const getStatusIcon = (status) => {
		const iconMap = {
			'pending': Clock,
			'in-process': Clock,
			'in-transit': Clock,
			'at-store': ShoppingBag,
			'authenticated': CheckCircle,
			'rejected': AlertTriangle,
			'disposed': AlertTriangle,
			'returned-to-seller': CheckCircle,
			0: Clock,
			1: CheckCircle,
			2: Clock,
			3: Clock,
			4: ShoppingBag,
			5: CheckCircle,
			6: AlertTriangle,
			7: AlertTriangle,
			8: CheckCircle,
			10: Truck
		};
		return iconMap[status] || AlertTriangle;
	};

	// Get status color
	const getStatusColor = (status) => {
		const statusMap = {
			'pending': '#F59E0B',
			'in-process': '#3B82F6',
			'in-transit': '#8B5CF6',
			'at-store': '#10B981',
			'authenticated': '#059669',
			'rejected': '#DC2626',
			'disposed': '#6B7280',
			'returned-to-seller': '#7C3AED',
			0: '#F59E0B',
			1: '#059669',
			2: '#3B82F6',
			3: '#8B5CF6',
			4: '#10B981',
			5: '#059669',
			6: '#DC2626',
			7: '#6B7280',
			8: '#7C3AED',
			10: '#8B5CF6'
		};
		return statusMap[status] || '#6B7280';
	};

	const paidReturnPayment = useMemo(() => {
		if (!Array.isArray(listing?.listing_return_payments)) return null;
		return listing.listing_return_payments.find(payment => {
			if (!payment || payment.status === undefined || payment.status === null) return false;
			return String(payment.status).toLowerCase() === 'paid';
		}) || null;
	}, [listing?.listing_return_payments]);

	useEffect(() => {
		setReturnToSellerForm({
			awbNumber: paidReturnPayment?.return_awb || '',
			courierName: paidReturnPayment?.return_courier || ''
		});
	}, [paidReturnPayment?.return_awb, paidReturnPayment?.return_courier]);

	const isReturnToSellerStatus = useMemo(() => {
		const statusCandidates = [
			listing?.status,
			listing?.status_text,
			listing?.dress_status_id,
			listing?.dressStatusId
		];

		return statusCandidates.some(value => {
			if (value === undefined || value === null) return false;
			const normalized = String(value).toLowerCase();
			return normalized === '8' ||
				normalized === 'returned to seller' ||
				normalized === 'returned-to-seller';
		});
	}, [listing?.dressStatusId, listing?.dress_status_id, listing?.status, listing?.status_text]);

	const canShowReturnToSellerButton = useMemo(() => {
		if (!(isSuperAdmin(user) || isStoreManager(user))) return false;
		if (!listing) return false;
		if (!paidReturnPayment) return false;

		const statusValue = Number(listing.status);
		const dressStatusValue = Number(listing.dress_status_id);
		const hasCourierDetails = Boolean(paidReturnPayment.return_awb) && Boolean(paidReturnPayment.return_courier);

		return statusValue === 8 && dressStatusValue === 8 && hasCourierDetails;
	}, [listing, paidReturnPayment, user]);

	const handleReturnToSeller = () => {
		if (!paidReturnPayment) {
			toast.error('Return payment not found for this listing.');
			return;
		}

		setReturnToSellerForm({
			awbNumber: paidReturnPayment?.return_awb || '',
			courierName: paidReturnPayment?.return_courier || ''
		});
		setShowReturnToSellerModal(true);
	};

	const submitReturnToSeller = async () => {
		if (!paidReturnPayment) {
			toast.error('Return payment not found for this listing.');
			return;
		}

		const awb = returnToSellerForm.awbNumber.trim();
		const courier = returnToSellerForm.courierName.trim();

		if (!awb) {
			toast.error('AWB number is required.');
			return;
		}

		if (!courier) {
			toast.error('Courier name is required.');
			return;
		}

		try {
			setReturnSubmitting(true);
			const response = await listingsService.returnToSeller(listingId, {
				awb_number: awb,
				courier_name: courier
			});

			if (response?.success) {
				toast.success(response?.message || 'Return dispatched details saved.');
				setShowReturnToSellerModal(false);
				await fetchListingDetails();
			} else {
				toast.error(response?.message || 'Failed to update return details.');
			}
		} catch (error) {
			console.error('Error marking return to seller:', error);
			toast.error(error?.message || 'Failed to update return details.');
		} finally {
			setReturnSubmitting(false);
		}
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined || value === '') return '₹0';
		const numericValue = Number(value);
		if (Number.isNaN(numericValue)) return '₹0';
		return `₹${numericValue.toLocaleString('en-IN')}`;
	};

	const formatDateTime = (value) => {
		if (!value) return 'N/A';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return 'N/A';
		return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
	};
	// Get status label from API data
	const getStatusLabel = (status) => {
		// First, try to find the status in the fetched dress statuses
		const dressStatus = dressStatuses.find(ds => ds.id === status);
		if (dressStatus) {
			return dressStatus.name;
		}
		
		// Fallback to hardcoded labels if API data is not available
		// const statusLabels = {
		// 	0: 'Pending',
		// 	1: 'Authenticated',
		// 	2: 'In Process',
		// 	3: 'In Transit',
		// 	4: 'At Store',
		// 	5: 'Authenticated',
		// 	6: 'Rejected',
		// 	7: 'Disposed',
		// 	8: 'Returned to Seller'
		// };
		// return statusLabels[status] || (typeof status === 'string' ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown');
	};

	// Helper function to get full image URL
	const getImageUrl = (path) => {
		if (!path) return null;
		// If path already starts with http, return as is
		if (path.startsWith('http')) return path;
		// Otherwise prepend the storage base URL
		const baseUrl = process.env.REACT_APP_API_URL || 'http://carsandbids-api.test';
		return `${baseUrl}/storage/${path}`;
	};

	// Fetch dress statuses from API
	const fetchDressStatuses = async () => {
		try {
			console.log('🔍 Fetching dress statuses...');
			const response = await dressStatusService.getAll();
			console.log('✅ Dress statuses fetched successfully:', response);
			
			if (response && response.data) {
				setDressStatuses(response.data);
			} else if (Array.isArray(response)) {
				setDressStatuses(response);
			}
		} catch (error) {
			console.error('❌ Error fetching dress statuses:', error);
			// Silently fail, will use fallback labels
		}
	};

	// Fetch listing details
	const fetchListingDetails = async () => {
		setLoading(true);
		setError(null);
		try {
			console.log('🔍 Fetching listing details for:', listingId);
			const response = await listingsService.getById(listingId);
			
			if (response.success) {
				console.log('✅ Listing details fetched successfully:', response.data);
				console.log('🔍 Response data structure:', {
					dressImages: response.data.dressImages,
					dress_images: response.data.dress_images,
					proof_of_purchase: response.data.proof_of_purchase,
					proof_images: response.data.proof_images,
					defect_images: response.data.defect_images,
					listing_content: response.data.listing_content
				});
				setListing(response.data);
				
				// Initialize edit state with existing values
				console.log('📝 Setting listing content:', response.data.listing_content);
				setListingContent(response.data.listing_content || '');
				
				// Initialize images - support both old and new field names
				const dressImages = Array.isArray(response.data.dressImages) ? response.data.dressImages : 
				                   Array.isArray(response.data.dress_images) ? response.data.dress_images : [];
				const proofImages = Array.isArray(response.data.proof_of_purchase) ? response.data.proof_of_purchase : 
				                   Array.isArray(response.data.proof_images) ? response.data.proof_images : 
				                   (response.data.proof_of_purchase && typeof response.data.proof_of_purchase === 'string') ? [{url: response.data.proof_of_purchase}] : [];
				const defectImages = Array.isArray(response.data.defect_images) ? response.data.defect_images : [];
				
				// Track which images are already redacted from database
				const redactedImageIds = new Set();
				dressImages.forEach(img => {
					if (img.is_redacted) {
						redactedImageIds.add(img.id);
					}
				});
				setRedactedImages(redactedImageIds);
				
				const allImages = [...dressImages, ...proofImages, ...defectImages];
				console.log('🔍 Image arrays created:', {
					dressImages: dressImages,
					proofImages: proofImages,
					defectImages: defectImages,
					allImages: allImages
				});
				setCurrentImageArray(allImages);
				setCurrentImageIndex(0);
			} else {
				setError('Failed to fetch listing details');
				showToast.error('Failed to fetch listing details');
			}
		} catch (error) {
			console.error('❌ Error fetching listing details:', error);
			setError('Error fetching listing details');
			showToast.error('Error fetching listing details');
		} finally {
			setLoading(false);
		}
	};

	// Fetch dress statuses on mount
	useEffect(() => {
		fetchDressStatuses();
	}, []);

	// Fetch listing details when listingId changes
	useEffect(() => {
		if (listingId) {
			fetchListingDetails();
		}
	}, [listingId]);

	// Image Modal Functions
	const openImageModal = (image, imageArray, index) => {
		setFullScreenImage(image);
		setCurrentImageArray(imageArray);
		setCurrentImageIndex(index);
	};

	const closeImageModal = () => {
		setFullScreenImage(null);
		setIsRedactionMode(false);
		setCurrentRedaction(null);
		setIsDrawing(false);
		setShowOriginal(false);
	};

	const navigateImage = (direction) => {
		if (direction === 'prev') {
			setCurrentImageIndex(prev =>
				prev === 0 ? currentImageArray.length - 1 : prev - 1
			);
		} else {
			setCurrentImageIndex(prev =>
				prev === currentImageArray.length - 1 ? 0 : prev + 1
			);
		}
		// Reset redaction drawing when changing images
		setCurrentRedaction(null);
		setIsDrawing(false);
		setShowOriginal(false);
	};

	// Redaction handlers
	const handleMouseDown = (e) => {
		if (!isRedactionMode) return;
		
		const rect = e.currentTarget.getBoundingClientRect();
		const x = ((e.clientX - rect.left) / rect.width) * 100;
		const y = ((e.clientY - rect.top) / rect.height) * 100;
		
		setIsDrawing(true);
		setCurrentRedaction({
			startX: x,
			startY: y,
			endX: x,
			endY: y
		});
	};

	const handleMouseMove = (e) => {
		if (!isRedactionMode || !isDrawing) return;
		
		const rect = e.currentTarget.getBoundingClientRect();
		const x = ((e.clientX - rect.left) / rect.width) * 100;
		const y = ((e.clientY - rect.top) / rect.height) * 100;
		
		setCurrentRedaction(prev => ({
			...prev,
			endX: x,
			endY: y
		}));
	};

	const handleMouseUp = () => {
		if (!isRedactionMode || !isDrawing || !currentRedaction) return;
		
		// Only save if the redaction has a reasonable size
		const width = Math.abs(currentRedaction.endX - currentRedaction.startX);
		const height = Math.abs(currentRedaction.endY - currentRedaction.startY);
		
		if (width > 1 && height > 1) {
			const currentImage = currentImageArray[currentImageIndex];
			const imageId = currentImage?.id || currentImageIndex;
			
			setRedactionAreas(prev => ({
				...prev,
				[imageId]: [...(prev[imageId] || []), {
					x: Math.min(currentRedaction.startX, currentRedaction.endX),
					y: Math.min(currentRedaction.startY, currentRedaction.endY),
					width: width,
					height: height
				}]
			}));
		}
		
		setIsDrawing(false);
		setCurrentRedaction(null);
	};

	const undoLastRedaction = () => {
		const currentImage = currentImageArray[currentImageIndex];
		const imageId = currentImage?.id || currentImageIndex;
		
		setRedactionAreas(prev => {
			const areas = prev[imageId] || [];
			if (areas.length === 0) return prev;
			
			return {
				...prev,
				[imageId]: areas.slice(0, -1)
			};
		});
	};

	const clearAllRedactions = () => {
		const currentImage = currentImageArray[currentImageIndex];
		const imageId = currentImage?.id || currentImageIndex;
		
		setRedactionAreas(prev => ({
			...prev,
			[imageId]: []
		}));
	};

	const saveRedactions = async () => {
		const currentImage = currentImageArray[currentImageIndex];
		const imageId = currentImage?.id || currentImageIndex;
		const redactions = redactionAreas[imageId] || [];
		
		if (redactions.length === 0) {
			showToast.error('No redactions to save');
			return;
		}

		if (!currentImage?.id) {
			showToast.error('Invalid image ID. Cannot save redactions.');
			return;
		}

		let loadingToast;
		try {
			loadingToast = showToast.loading('Applying redactions to image...');
			
			const response = await listingsService.applyImageRedactions(
				currentImage.id,
				redactions
			);

			// Dismiss loading toast
			if (loadingToast) {
				toast.dismiss(loadingToast);
			}

			if (response.success) {
				showToast.success(`Redactions applied successfully! ${response.data.redactions_count} area(s) redacted.`);
				console.log('✅ Redaction results:', response.data);
				console.log('📁 Backup paths saved to DB:', response.data.backup_paths);
				
				// Clear the redactions from state for this image
				setRedactionAreas(prev => ({
					...prev,
					[imageId]: []
				}));
				
				// Close and reopen modal to force image refresh
				const tempImageIndex = currentImageIndex;
				const tempImageArray = currentImageArray;
				closeImageModal();
				
				// Refresh the listing to get updated data from server
				const refreshResponse = await listingsService.getById(listingId);
				if (refreshResponse.success) {
					console.log('🔄 Listing refreshed with updated images');
					setListing(refreshResponse.data);
					
					// Update image arrays
					const dressImages = Array.isArray(refreshResponse.data.dressImages) ? refreshResponse.data.dressImages : 
					                   Array.isArray(refreshResponse.data.dress_images) ? refreshResponse.data.dress_images : [];
					const proofImages = Array.isArray(refreshResponse.data.proof_of_purchase) ? refreshResponse.data.proof_of_purchase : 
					                   Array.isArray(refreshResponse.data.proof_images) ? refreshResponse.data.proof_images : 
					                   (refreshResponse.data.proof_of_purchase && typeof refreshResponse.data.proof_of_purchase === 'string') ? [{url: refreshResponse.data.proof_of_purchase}] : [];
					const defectImages = Array.isArray(refreshResponse.data.defect_images) ? refreshResponse.data.defect_images : [];
					
					// Track redacted images
					const redactedImageIds = new Set();
					dressImages.forEach(img => {
						if (img.is_redacted) {
							redactedImageIds.add(img.id);
						}
					});
					setRedactedImages(redactedImageIds);
					
					const allImages = [...dressImages, ...proofImages, ...defectImages];
					setCurrentImageArray(allImages);
					
					// Reopen the modal with the refreshed image
					setTimeout(() => {
						const updatedImage = allImages[tempImageIndex];
						if (updatedImage) {
							openImageModal(updatedImage, allImages, tempImageIndex);
							showToast.success('Image updated! Click "Original" button to compare versions.');
						}
					}, 300);
				}

				// Exit redaction mode
				setIsRedactionMode(false);
			} else {
				showToast.error(response.message || 'Failed to apply redactions');
			}
		} catch (error) {
			// Dismiss loading toast if it exists
			if (loadingToast) {
				toast.dismiss(loadingToast);
			}
			console.error('❌ Error applying redactions:', error);
			showToast.error(error.message || 'Failed to apply redactions');
		}
	};

	// Authentication Functions
	const handleStarClick = (param, rating) => {
		setAuthParams(prev => ({
			...prev,
			[param]: rating
		}));
	};

	const getAverageRating = () => {
		const values = Object.values(authParams);
		const nonZeroValues = values.filter(val => val > 0);
		return nonZeroValues.length > 0
			? (nonZeroValues.reduce((sum, val) => sum + val, 0) / nonZeroValues.length).toFixed(1)
			: 0;
	};

	const submitAuthenticationDecision = async (decision) => {
		if (!listing) {
			showToast.error('Listing details are not available. Please refresh and try again.');
			return;
		}

		if (!user?.id) {
			toast.error('Your session has expired. Please log in again.');
			return;
		}

		const estimateValue = overallEstimate !== '' && overallEstimate !== null ? Number(overallEstimate) : NaN;
		if (!Number.isFinite(estimateValue) || estimateValue <= 0) {
			toast.error('Overall estimate is required and must be greater than 0.');
			return;
		}

		try {
			setSubmittingAuthentication(true);
			toast.loading(
				decision === 'authenticated' ? 'Submitting authentication approval...' : 'Submitting rejection...',
				{ id: 'listing-authentication' }
			);

			const payload = {
				listing_id: listingId,
				authenticatorId: user?.id,
				status: decision,
				rating_purchase_proof: authParams.purchaseProof || null,
				rating_fabric: authParams.fabric || null,
				rating_stitching: authParams.stitching || null,
				rating_labels: authParams.labels || null,
				rating_condition: authParams.condition || null,
				average_rating: Number(getAverageRating()) || null,
				total_estimated_cost: estimateValue,
				review_comment: overallRemark || null,
			};

			const response = await purchaseService.submitAuthenticationReview(payload);

			if (response?.success) {
				toast.success(
					decision === 'authenticated'
						? 'Listing marked as authenticated successfully.'
						: 'Listing rejected and marked for return to seller.',
					{ id: 'listing-authentication' }
				);
				await fetchListingDetails();
			} else {
				toast.error(response?.message || 'Failed to submit authentication review.', {
					id: 'listing-authentication'
				});
			}
		} catch (error) {
			toast.error(error?.message || 'Failed to submit authentication review.', {
				id: 'listing-authentication'
			});
		} finally {
			setSubmittingAuthentication(false);
		}
	};

	const handleMarkAuthenticated = () => {
		submitAuthenticationDecision('authenticated');
	};

	const handleReject = () => {
		submitAuthenticationDecision('rejected');
	};

	// Handle edit listing
	const handleEdit = () => {
		navigate(`/listings/edit/${listingId}`);
	};

	// Handle delete listing
	const handleDelete = () => {
		if (window.confirm('Are you sure you want to delete this listing?')) {
			// TODO: Implement delete functionality
			showToast.success('Delete functionality will be implemented');
		}
	};

	// Handle send to listing
	const handleSendToListing = async () => {
		setSendingToListing(true);
		try {
			// Find the "listed" status ID from dressStatuses
			const listedStatus = dressStatuses.find(ds => ds.name.toLowerCase() === 'listed');
			const listedStatusId = listedStatus ? listedStatus.id : 9; // Fallback to 9 if not found
			if (!selectedBranchId) {
				showToast.error('Please select a branch to list the item at');
				setSendingToListing(false);
				return;
			}
			console.log('🚀 Sending listing to listed status:', listedStatusId, 'branch:', selectedBranchId);
			
			// Update dress status first
			const statusResponse = await listingsService.updateDressStatus(listingId, {
				dress_status_id: listedStatusId
			});
			
			if (!statusResponse.success) {
				showToast.error('Failed to update dress status');
				setSendingToListing(false);
				return;
			}
			
			// Assign branch separately
			const branchResponse = await listingsService.assignBranch(listingId, selectedBranchId);
			
			const response = branchResponse;
			
			if (response.success) {
				showToast.success('Listing sent to live successfully!');
				setShowSendToListingModal(false);
				// Refresh listing details to show updated status
				fetchListingDetails();
			} else {
				showToast.error('Failed to send listing to live');
			}
		} catch (error) {
			console.error('❌ Error sending to listing:', error);
			showToast.error('Error sending listing to live');
		} finally {
			setSendingToListing(false);
		}
	};

	// Load branches on mount and whenever modal opens
	useEffect(() => {
		const loadBranches = async () => {
			try {
				setBranchesLoading(true);
				const res = await branchService.getAll({ per_page: 1000 });
				const list = res?.data ? (Array.isArray(res.data) ? res.data : (res.data.data || [])) : [];
				setBranches(list);
			} catch (e) {
				console.error('Error loading branches:', e);
				showToast.error('Failed to load branches');
			} finally {
				setBranchesLoading(false);
			}
		};
		loadBranches();
	}, []);

	// Initialize default branch selection from listing details when available
	useEffect(() => {
		if (listing && listing.branch_id && !selectedBranchId) {
			setSelectedBranchId(listing.branch_id);
		}
	}, [listing]);

	useEffect(() => {
		if (showSendToListingModal && branches.length === 0 && !branchesLoading) {
			// Attempt reload when opening modal if not loaded yet
			(async () => {
				try {
					setBranchesLoading(true);
					const res = await branchService.getAll({ per_page: 1000 });
					const list = res?.data ? (Array.isArray(res.data) ? res.data : (res.data.data || [])) : [];
					setBranches(list);
				} finally {
					setBranchesLoading(false);
				}
			})();
		}
	}, [showSendToListingModal, branches.length, branchesLoading]);

	// Save branch assignment directly from Listing Details
	const handleSaveBranch = async () => {
		if (!selectedBranchId) {
			showToast.error('Please select a branch');
			return;
		}
		try {
			showToast.loading('Updating branch...', { id: 'update-branch' });
			const res = await listingsService.assignBranch(listingId, selectedBranchId);
			if (res?.success) {
				showToast.success('Branch updated successfully', { id: 'update-branch' });
				// Optionally refresh details if API returns updated linkage
				fetchListingDetails?.();
			} else {
				showToast.error(res?.message || 'Failed to update branch', { id: 'update-branch' });
			}
		} catch (e) {
			console.error('Error updating branch:', e);
			showToast.error('Error updating branch', { id: 'update-branch' });
		}
	};

	// Handle de-list
	const handleDelist = async () => {
		setDelisting(true);
		try {
			console.log('🗑️ De-listing listing:', listingId);
			const response = await listingsService.delistListing(listingId);
			
			if (response.success) {
				showToast.success('Listing de-listed successfully!');
				setShowDelistModal(false);
				// Refresh listing details to show updated status
				fetchListingDetails();
			} else {
				showToast.error(response.message || 'Failed to de-list listing');
			}
		} catch (error) {
			console.error('❌ Error de-listing:', error);
			showToast.error(error.response?.data?.message || 'Error de-listing listing');
		} finally {
			setDelisting(false);
		}
	};

	// Handle toggle featured status
	const handleToggleFeatured = async () => {
		setTogglingFeatured(true);
		try {
			console.log('⭐ Toggling featured status for listing:', listingId);
			const response = await listingsService.toggleFeatured(listingId);
			
			if (response.success) {
				showToast.success(response.message || 'Featured status updated successfully!');
				// Refresh listing details to show updated featured status
				fetchListingDetails();
			} else {
				showToast.error(response.message || 'Failed to update featured status');
			}
		} catch (error) {
			console.error('❌ Error toggling featured status:', error);
			showToast.error(error.response?.data?.message || 'Error updating featured status');
		} finally {
			setTogglingFeatured(false);
		}
	};

	// Star Rating Component
	const StarRating = ({ param, label, value, onChange }) => (
		<div style={{ marginBottom: '0px' }}>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
				<label style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
					{label}
				</label>
				<div style={{ fontSize: '12px', color: '#6b7280' }}>
					{value > 0 ? `${value}/5` : 'Not rated'}
				</div>
			</div>
			<div style={{ display: 'flex', gap: '4px' }}>
				{[1, 2, 3, 4, 5].map((star) => (
					<button
						key={star}
						type="button"
						onClick={() => onChange(star)}
						style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							padding: '2px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
					>
						<Star
							size={20}
							fill={star <= value ? '#fbbf24' : 'none'}
							color={star <= value ? '#fbbf24' : '#d1d5db'}
						/>
					</button>
				))}
			</div>
		</div>
	);

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<Loader className="animate-spin mx-auto mb-4" size={48} />
					<p className="text-gray-600">Loading listing details...</p>
				</div>
			</div>
		);
	}

	if (error || !listing) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<AlertTriangle className="mx-auto mb-4 text-red-500" size={48} />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">Listing Not Found</h2>
					<p className="text-gray-600 mb-4">{error || 'The listing you are looking for does not exist.'}</p>
					<button
						onClick={() => navigate('/listings')}
						className="btn btn-primary"
					>
						<ArrowLeft size={16} className="mr-2" />
						Back to Listings
					</button>
				</div>
			</div>
		);
	}

	// Ensure listing has the expected structure
	if (!listing || typeof listing !== 'object') {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<AlertTriangle className="mx-auto mb-4 text-red-500" size={48} />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Listing Data</h2>
					<p className="text-gray-600 mb-4">The listing data is not in the expected format.</p>
					<button
						onClick={() => navigate('/listings')}
						className="btn btn-primary"
					>
						<ArrowLeft size={16} className="mr-2" />
						Back to Listings
					</button>
				</div>
			</div>
		);
	}

	const StatusIcon = getStatusIcon(listing?.status);
	const statusColor = getStatusColor(listing?.status);

	return (
		<div className="page-container">
			{/* Custom Styles for Rich Text Editor */}
			<style>{`
				.ql-toolbar {
					border: 1px solid #d1d5db;
					border-radius: 6px 6px 0 0;
				}
				.ql-container {
					border: 1px solid #d1d5db;
					border-top: none;
					border-radius: 0 0 6px 6px;
					font-size: 14px;
				}
				.ql-editor {
					min-height: 100px;
				}
				.ql-snow .ql-editor {
					padding: 0;
				}
			`}</style>
			{/* Header */}
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
				<button
					onClick={() => navigate('/listings')}
					className="btn btn-secondary"
				>
					<ArrowLeft size={16} style={{ marginRight: '8px' }} />
					Back to Listings
				</button>
				
				<div style={{ display: 'flex', gap: '12px' }}>

					{/* Return to Seller button - visible when status indicates return & payment paid */}
					{canShowReturnToSellerButton && (
						<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
							<button
								onClick={handleReturnToSeller}
								className="btn"
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									backgroundColor: '#7c3aed',
									color: 'white',
									border: '1px solid #7c3aed',
									justifyContent: 'center'
								}}
							>
								<Truck size={16} />
								Return to Seller
							</button>
						</div>
					)}
					
					{/* Featured Toggle Button - Visible only when status <= 2 */}
					{isSuperAdmin(user) && listing?.status !== undefined && listing?.status <= 2 && (
						<button
							onClick={handleToggleFeatured}
							disabled={togglingFeatured}
							className="btn"
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								backgroundColor: listing?.is_featured ? '#f59e0b' : '#6b7280',
								color: 'white',
								border: `1px solid ${listing?.is_featured ? '#f59e0b' : '#6b7280'}`,
								opacity: togglingFeatured ? 0.6 : 1
							}}
						>
							<Star size={16} fill={listing?.is_featured ? 'white' : 'none'} />
							{togglingFeatured ? 'Updating...' : (listing?.is_featured ? 'Remove Featured' : 'Toggle Featured')}
						</button>
					)}

									{/* Show "Send to Listing" if status < 2, otherwise show "De-list" if status >= 2 */}
				{isSuperAdmin(user) && listing?.status !== undefined && listing?.status <= 2 && listing?.status !== 9 && listing?.status >= 2 ? (
					/* De-list Button - Shows when status >= 2 (Listed and beyond) */
					<button
						onClick={() => setShowDelistModal(true)}
						className="btn"
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							backgroundColor: '#ef4444',
							color: 'white',
							border: '1px solid #ef4444'
						}}
					>
						<Trash2 size={16} />
						De-list
					</button>
				) : isSuperAdmin(user) && listing?.status < 2 || isSuperAdmin(user) && listing?.status == 9 ? (
					/* Send to Listing Button - Shows when status < 2 (Pending) */
					<button
						onClick={() => setShowSendToListingModal(true)}
						className="btn btn-success"
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px'
						}}
					>
						<CheckCircle size={16} />
						Send to Listing
					</button>
				) : null}
				</div>
			</div>
			
			{/* Featured Status Indicator */}
			{listing?.is_featured && (
				<div style={{
					backgroundColor: '#fef3c7',
					border: '1px solid #f59e0b',
					borderRadius: '8px',
					padding: '12px 16px',
					marginBottom: '24px',
					display: 'flex',
					alignItems: 'center',
					gap: '8px'
				}}>
					<Star size={20} fill="#f59e0b" color="#f59e0b" />
					<span style={{ color: '#92400e', fontWeight: '600', fontSize: '14px' }}>
						This listing is currently featured and will appear prominently on the website
					</span>
				</div>
			)}
			
			<div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
				{/* Item Info Cards */}
				<div style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr 1fr',
					gap: '24px',
					marginBottom: '32px'
				}}>
					<div style={{
						border: '1px solid #e5e7eb',
						borderRadius: '8px',
						padding: '16px',
						backgroundColor: '#f9fafb'
					}}>
						<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
							Seller Information 2
						</h3>
						<div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span style={{ color: '#6b7280' }}>Name:</span>
								<span style={{ fontWeight: '500' }}>{listing?.seller?.full_name || listing?.seller_name || 'N/A'}</span>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span style={{ color: '#6b7280' }}>Email:</span>
								<span style={{ fontWeight: '500' }}>{listing?.seller?.email || listing?.seller_email || 'N/A'}</span>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span style={{ color: '#6b7280' }}>Mobile:</span>
								<span style={{ fontWeight: '500', fontFamily: 'monospace' }}>{listing?.seller?.mobile || 'N/A'}</span>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span style={{ color: '#6b7280' }}>Location:</span>
								<span style={{ fontWeight: '500' }}>{listing?.seller?.city && listing?.seller?.state ? `${listing?.seller.city}, ${listing?.seller.state}` : 'N/A'}</span>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span style={{ color: '#6b7280' }}>PIN Code:</span>
								<span style={{ fontWeight: '500', fontFamily: 'monospace' }}>{listing?.seller?.pin || 'N/A'}</span>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span style={{ color: '#6b7280' }}>Member Since:</span>
								<span style={{ fontWeight: '500' }}>{listing?.seller?.created_at ? new Date(listing?.seller.created_at).toLocaleDateString('en-IN') : listing?.created_at ? new Date(listing?.created_at).toLocaleDateString('en-IN') : 'N/A'}</span>
							</div>
						</div>
					</div>

					<div style={{
						border: '1px solid #e5e7eb',
						borderRadius: '8px',
						padding: '16px',
						backgroundColor: '#ffffff'
					}}>
						<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
							Status Information
						</h3>
						<div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span style={{ color: '#6b7280' }}>Current Status:</span>
								<span style={{
									backgroundColor: getStatusColor(listing?.status),
									color: 'white',
									padding: '2px 8px',
									borderRadius: '12px',
									fontSize: '12px',
									fontWeight: '500',
									display: 'flex',
									alignItems: 'center',
									gap: '4px'
								}}>
									<StatusIcon size={12} />
									{getStatusLabel(listing?.status)}
								</span>
							</div>
							{listing?.condition && (
								<div style={{ }}>
									
									{/* <span style={{ fontWeight: '500' }}>{renderValue(listing?.condition)}</span> */}
									<div style={{ display: 'flex', justifyContent: 'space-between',marginTop: 10 }}>
										<span style={{ color: '#6b7280' }}>Times Worn:</span>
										<span style={{ fontWeight: '500' }}>{(listing?.condition.times_worn)}</span>
									</div>
									<div style={{ display: 'flex', justifyContent: 'space-between',marginTop: 10 }}>
										<span style={{ color: '#6b7280' }}>Condition Rating:</span>
										<span style={{ fontWeight: '500' }}>{(listing?.condition.condition_rating)}</span>
									</div>
									<div style={{ display: 'flex', justifyContent: 'space-between',marginTop: 10 }}>
										<span style={{ color: '#6b7280' }}>Defects:</span>
											<span style={{ fontWeight: '500' }}>{Array.isArray(listing?.condition.defects) ? listing?.condition.defects.join(', ') : listing?.condition.defects}</span>
										</div>
									<div style={{ display: 'flex', justifyContent: 'space-between',marginTop: 10 }}>
										<span style={{ color: '#6b7280' }}>Care Instructions:</span>
										<span style={{ fontWeight: '500' }}>{(listing?.condition.care_instructions)}</span>
									</div>
									<div style={{ display: 'flex', justifyContent: 'space-between',marginTop: 10 }}>
										<span style={{ color: '#6b7280' }}>Additional Notes:</span>
										<span style={{ fontWeight: '500' }}>{(listing?.condition.additional_notes)}</span>
									</div>
								</div>
							)}
							{listing?.sizing_fit && (
								<>
									<div style={{ display: 'flex', justifyContent: 'space-between' }}>
										<span style={{ color: '#6b7280' }}>Size:</span>
										<span style={{ fontWeight: '500' }}>{('size' in listing?.sizing_fit) ? listing?.sizing_fit.size : '--'}</span>
									</div>
									<div style={{ display: 'flex', justifyContent: 'space-between' }}>
										<span style={{ color: '#6b7280' }}>Fit:</span>
										<span style={{ fontWeight: '500' }}>{('fit' in listing?.sizing_fit) ? listing?.sizing_fit.fit : '--'}</span>
									</div>
									<div style={{ display: 'flex', justifyContent: 'space-between' }}>
										<span style={{ color: '#6b7280' }}>Measurements:</span>
										<span style={{ fontWeight: '500' }}>{('measurements' in listing?.sizing_fit) ? listing?.sizing_fit.measurements : '--'}</span>
									</div>
									<div style={{ display: 'flex', justifyContent: 'space-between' }}>
										<span style={{ color: '#6b7280' }}>Additional Notes:</span>
										<span style={{ fontWeight: '500' }}>{('additional_notes' in listing?.sizing_fit) ? listing?.sizing_fit.additional_notes : '--'}</span>
									</div>
								</>
							)}
							{listing?.price && (
								<div style={{ display: 'flex', justifyContent: 'space-between' }}>
									<span style={{ color: '#6b7280' }}>Price:</span>
									<span style={{ fontWeight: '500' }}>₹{listing?.price}</span>
								</div>
							)}
						</div>
					</div>

					{/* Pricing Information */}
					{listing?.pricing && (
						<div style={{
							border: '1px solid #e5e7eb',
							borderRadius: '8px',
							padding: '16px',
							backgroundColor: '#ffffff',
						}}>
							{paidReturnPayment?.return_awb && (
						<div style={{
							backgroundColor: '#f5f3ff',
							border: '1px solid #ddd6fe',
							borderRadius: '6px',
							padding: '8px 12px',
							fontSize: '12px',
							color: '#4c1d95',
							textAlign: 'left'
						}}>
							<p style={{ margin: 0, fontWeight: '600' }}>Return Fees Paid</p>
							<p style={{ margin: '4px 0 0 0' }}>
								<strong>Amount:</strong> {formatCurrency(paidReturnPayment.amount)}
							</p>
							<p style={{ margin: '2px 0 0 0' }}>
								<strong>Paid On:</strong> {formatDateTime(paidReturnPayment.paid_at || paidReturnPayment.updated_at)}
							</p>
							{paidReturnPayment.payment_id ? (
								<p style={{ margin: '2px 0 0 0' }}>
									<strong>Payment ID:</strong> {paidReturnPayment.payment_id}
								</p>
							) : null}
							{paidReturnPayment.order_id ? (
								<p style={{ margin: '2px 0 0 0' }}>
									<strong>Order ID:</strong> {paidReturnPayment.order_id}
								</p>
							) : null}
							{paidReturnPayment.return_courier ? (
								<p style={{ margin: '2px 0 0 0' }}>
									<strong>Courier:</strong> {paidReturnPayment.return_courier}
								</p>
							) : null}
							{paidReturnPayment.return_awb ? (
								<p style={{ margin: '2px 0 0 0' }}>
									<strong>AWB:</strong> {paidReturnPayment.return_awb}
								</p>
							) : null}
							{paidReturnPayment.dispatched_at ? (
								<p style={{ margin: '2px 0 0 0' }}>
									<strong>Dispatched On:</strong> {formatDateTime(paidReturnPayment.dispatched_at)}
								</p>
							) : null}
						</div>
					)}
							<div style={{ marginTop: 20 }}>
								<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
									Pricing Information
								</h3>
								<div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
									{listing?.pricing.ask_price && (
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Asking Price:</span>
											<span style={{ fontWeight: '600', color: '#059669', fontSize: '16px' }}>₹{parseFloat(listing?.pricing.ask_price).toLocaleString('en-IN')}</span>
										</div>
									)}
									{listing?.pricing.pickup_address && (
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
											<span style={{ color: '#6b7280' }}>Pickup Address:</span>
											<span style={{ fontWeight: '500', textAlign: 'right', maxWidth: '60%' }}>{listing?.pricing.pickup_address}</span>
										</div>
									)}
									{listing?.pricing.terms_conditions_agreed !== undefined && (
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Terms Accepted:</span>
											<span style={{
												backgroundColor: listing?.pricing.terms_conditions_agreed ? '#10b981' : '#ef4444',
												color: 'white',
												padding: '2px 8px',
												borderRadius: '12px',
												fontSize: '12px',
												fontWeight: '500'
											}}>
												{listing?.pricing.terms_conditions_agreed ? 'Yes' : 'No'}
											</span>
										</div>
									)}
									{/* {listing?.pricing.status !== undefined && (
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Pricing Status:</span>
											<span style={{
												backgroundColor: listing?.pricing.status === 1 ? '#10b981' : '#6b7280',
												color: 'white',
												padding: '2px 8px',
												borderRadius: '12px',
												fontSize: '12px',
												fontWeight: '500'
											}}>
												{listing?.pricing.status === 1 ? 'Active' : 'Inactive'}
											</span>
										</div>
									)} */}
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Item Details */}
				<div style={{ marginBottom: '24px' }}>
					<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
						Item Details
					</h3>
					<div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
						<table style={{ width: '100%', borderCollapse: 'collapse' }}>
							<thead>
								<tr style={{ backgroundColor: '#f9fafb' }}>
									<th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Property</th>
									<th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Value</th>
								</tr>
							</thead>
							<tbody>
								{(listing?.designer_brand || listing?.brand) && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Designer Brand</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.designer_brand || listing?.brand)}</td>
									</tr>
								)}
								{listing?.category && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Category</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.category)}</td>
									</tr>
								)}
								{(listing?.colors || listing?.color) && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Colors</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.colors || listing?.color)}</td>
									</tr>
								)}
								{(listing?.fabrics || listing?.fabric) && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Fabrics</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.fabrics || listing?.fabric)}</td>
									</tr>
								)}
								{listing?.work_embroidery && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Work/Embroidery</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.work_embroidery)}</td>
									</tr>
								)}
								{listing?.original_price && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Original Price</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>₹{parseFloat(listing?.original_price).toLocaleString('en-IN')}</td>
									</tr>
								)}
								{listing?.purchase_date && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Purchase Date</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{new Date(listing?.purchase_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</td>
									</tr>
								)}
								{listing?.original_tags_present !== undefined && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Original Tags Present</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>
											<span style={{
												backgroundColor: listing?.original_tags_present ? '#10b981' : '#ef4444',
												color: 'white',
												padding: '2px 8px',
												borderRadius: '12px',
												fontSize: '12px',
												fontWeight: '500'
											}}>
												{listing?.original_tags_present ? 'Yes' : 'No'}
											</span>
										</td>
									</tr>
								)}
								{(listing?.size || listing?.sizing_fit?.size) && (
									<tr style={{ borderTop: '1px solid #e5e7eb' }}>
										<td style={{ padding: '12px', color: '#6b7280' }}>Size</td>
										<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.size || listing?.sizing_fit?.size)}</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>

				{/* Images Section */}
				<div style={{
					display: 'grid',
					gridTemplateColumns: (listing?.dressImages?.length > 0 || listing?.dress_images?.length > 0) && (listing?.proof_of_purchase?.length > 0 || listing?.proof_images?.length > 0) && listing?.defect_images?.length > 0 ? '1fr 1fr 1fr' : (listing?.dressImages?.length > 0 || listing?.dress_images?.length > 0) && (listing?.proof_of_purchase?.length > 0 || listing?.proof_images?.length > 0) ? '1fr 1fr' : '1fr',
					gap: '24px',
					marginBottom: '24px'
				}}>
					{/* Dress Images */}
					{(listing?.dressImages?.length > 0 || listing?.dress_images?.length > 0) && (
						<div>
							<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
								Dress Images ({(listing?.dressImages || listing?.dress_images)?.length})
							</h3>
							<div style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
								gap: '8px'
							}}>
								
								{(listing?.dressImages || listing?.dress_images)?.map((image, index) => (
									<div key={image.id || index} style={{ position: 'relative' }}>
										<div style={{
											position: 'relative',
											width: '100%',
											paddingBottom: '100%',
											backgroundColor: '#f3f4f6',
											borderRadius: '4px',
											border: '1px solid #e5e7eb',
											overflow: 'hidden'
										}}>
											<img
												src={getImageUrl(image.medium_path) || image.medium_path || image.url || image.image_url}
												alt={image.image_type || image.alt || `Dress Image ${index + 1}`}
												style={{
													position: 'absolute',
													top: 0,
													left: 0,
													width: '100%',
													height: '100%',
													objectFit: 'cover',
													cursor: 'pointer'
												}}
												onClick={() => openImageModal(image, listing?.dressImages || listing?.dress_images, index)}
											/>
											{redactedImages.has(image.id) && (
												<div 
													onClick={(e) => {
														e.stopPropagation();
														openImageModal(image, listing?.dressImages || listing?.dress_images, index);
													}}
													style={{
														position: 'absolute',
														top: '4px',
														right: '4px',
														backgroundColor: '#ef4444',
														color: 'white',
														padding: '2px 6px',
														borderRadius: '4px',
														fontSize: '9px',
														fontWeight: '600',
														display: 'flex',
														alignItems: 'center',
														gap: '2px',
														boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
														cursor: 'pointer',
														zIndex: 10,
														transition: 'transform 0.2s'
													}}
													onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
													onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
													title="Click to view redacted image"
												>
													<Edit size={10} />
													REDACTED
												</div>
											)}
										</div>
										{image.image_type && (
											<div style={{
												marginTop: '4px',
												fontSize: '10px',
												color: '#6b7280',
												textAlign: 'center',
												textTransform: 'capitalize'
											}}>
												{image.image_type.replace(/_/g, ' ')}
											</div>
										)}
									</div>
								))}
							</div>
						</div>
					)}

					{/* Proof of Purchase Images */}
					{(typeof listing?.proof_of_purchase == 'object') &&(
						(listing?.proof_of_purchase?.length > 0 || listing?.proof_images?.length > 0) && (
							<div>
								<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
									Proof of Purchase ({(listing?.proof_of_purchase || listing?.proof_images)?.length})
								</h3>
								<div style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
									gap: '8px'
								}}>
									{(listing?.proof_of_purchase || listing?.proof_images)?.map((image, index) => (
										<div key={image.id || index} style={{ position: 'relative' }}>
											<div style={{
												position: 'relative',
												width: '100%',
												paddingBottom: '100%',
												backgroundColor: '#f3f4f6',
												borderRadius: '4px',
												border: '1px solid #e5e7eb',
												overflow: 'hidden'
											}}>
												<img
													src={getImageUrl(image.medium_path) || image.medium_path || image.url || image.image_url}
													alt={image.image_type || image.alt || `Proof Image ${index + 1}`}
													style={{
														position: 'absolute',
														top: 0,
														left: 0,
														width: '100%',
														height: '100%',
														objectFit: 'cover',
														cursor: 'pointer'
													}}
													onClick={() => openImageModal(image, listing?.proof_of_purchase || listing?.proof_images, index)}
												/>
											</div>
											{image.image_type && (
												<div style={{
													marginTop: '4px',
													fontSize: '10px',
													color: '#6b7280',
													textAlign: 'center',
													textTransform: 'capitalize'
												}}>
													{image.image_type.replace(/_/g, ' ')}
												</div>
											)}
										</div>
									))}
								</div>
							</div>
						)
					)}
					{(typeof listing?.proof_of_purchase == 'string') &&(
						<div>
							<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
								Proof of Purchase 
							</h3>
							<div style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
								gap: '8px'
							}}>
								<img 
									src={getImageUrl(listing?.proof_of_purchase)} alt="Proof of Purchase" 
									style={{
										width: '100%',
										height: '100%',
										objectFit: 'cover',
										borderRadius: '4px',
										border: '1px solid #e5e7eb',
										cursor: 'pointer'
									}}
									
								/>
							</div>
						</div>
					)}


					{/* Defect Images */}
					{listing?.defect_images?.length > 0 && (
						<div>
							<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
								Defect Images ({(listing?.defect_images || []).length})
							</h3>
							<div style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
								gap: '8px'
							}}>
								{(listing?.defect_images || [])?.map((image, index) => (
									<div key={image.id || index} style={{ position: 'relative' }}>
										<div style={{
											position: 'relative',
											width: '100%',
											paddingBottom: '100%',
											backgroundColor: '#f3f4f6',
											borderRadius: '4px',
											border: '1px solid #e5e7eb',
											overflow: 'hidden'
										}}>
											<img
												src={getImageUrl(image.medium_path) || image.medium_path || image.url || image.image_url}
												alt={image.image_type || image.alt || `Defect Image ${index + 1}`}
												style={{
													position: 'absolute',
													top: 0,
													left: 0,
													width: '100%',
													height: '100%',
													objectFit: 'cover',
													cursor: 'pointer'
												}}
												onClick={() => openImageModal(image, listing?.defect_images, index)}
											/>
										</div>
										{image.image_type && (
											<div style={{
												marginTop: '4px',
												fontSize: '10px',
												color: '#6b7280',
												textAlign: 'center',
												textTransform: 'capitalize'
											}}>
												{image.image_type.replace(/_/g, ' ')}
											</div>
										)}
									</div>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Description */}
				{listing?.description && (
					<div style={{ marginBottom: '24px' }}>
						<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
							Additional Information
						</h3>
						<div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
							<table style={{ width: '100%', borderCollapse: 'collapse' }}>
								<tbody>
									{typeof listing?.description === 'object' ? (
										<>
											{listing?.description.times_worn !== undefined && (
												<tr style={{ borderTop: '1px solid #e5e7eb' }}>
													<td style={{ padding: '12px', color: '#6b7280', width: '30%' }}>Times Worn</td>
													<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.description.times_worn)}</td>
												</tr>
											)}
											{listing?.description.condition_rating !== undefined && (
												<tr style={{ borderTop: '1px solid #e5e7eb' }}>
													<td style={{ padding: '12px', color: '#6b7280', width: '30%' }}>Condition Rating</td>
													<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.description.condition_rating)}</td>
												</tr>
											)}
											{listing?.description.defects && (
												<tr style={{ borderTop: '1px solid #e5e7eb' }}>
													<td style={{ padding: '12px', color: '#6b7280', width: '30%' }}>Defects</td>
													<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.description.defects)}</td>
												</tr>
											)}
											{listing?.description.care_instructions && (
												<tr style={{ borderTop: '1px solid #e5e7eb' }}>
													<td style={{ padding: '12px', color: '#6b7280', width: '30%' }}>Care Instructions</td>
													<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.description.care_instructions)}</td>
												</tr>
											)}
											{listing?.description.additional_notes && (
												<tr style={{ borderTop: '1px solid #e5e7eb' }}>
													<td style={{ padding: '12px', color: '#6b7280', width: '30%' }}>Additional Notes</td>
													<td style={{ padding: '12px', color: '#111827', fontWeight: '500' }}>{renderValue(listing?.description.additional_notes)}</td>
												</tr>
											)}
										</>
									) : (
										<tr>
											<td style={{ padding: '12px', color: '#111827' }}>{renderValue(listing?.description)}</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				)}

				{/* Branch Assignment */}
				<div style={{ marginBottom: '24px' }}>
					<div style={{
						backgroundColor: 'white',
						border: '1px solid #e5e7eb',
						borderRadius: '8px',
						padding: '20px',
						boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
					}}>
						<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
							Assigned Store/Branch
						</h3>
						<div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
							<select
								value={selectedBranchId || ''}
								onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : null)}
								className="form-select"
								style={{ minWidth: '260px', height: '40px' }}
								disabled={branchesLoading}
							>
								<option value="">{branchesLoading ? 'Loading branches...' : 'Select a branch'}</option>
								{branches.map((b) => (
									<option key={b.id} value={b.id}>
										{b.branch_name || b.name || `Branch #${b.id}`}
									</option>
								))}
							</select>
							<button
								onClick={handleSaveBranch}
								className="btn btn-primary"
								disabled={!selectedBranchId}
							>
								Save
							</button>
						</div>
					</div>
				</div>

				{/* Authentication Section - Only for authenticator role */}
				{isAuthenticator(user) && (
					<div style={{ marginBottom: '24px' }}>
						{/* Authentication Assessment Card */}
						<div style={{
							backgroundColor: 'white',
							border: '1px solid #e5e7eb',
							borderRadius: '8px',
							padding: '20px',
							marginBottom: '16px',
							boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
						}}>
							<h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#111827' }}>
								Authentication Assessment
							</h3>

							<div style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '16px'
							}}>
								<StarRating
									param="purchaseProof"
									label="Purchase Proof"
									value={authParams.purchaseProof}
									onChange={(rating) => handleStarClick('purchaseProof', rating)}
								/>

								<StarRating
									param="fabric"
									label="Fabric"
									value={authParams.fabric}
									onChange={(rating) => handleStarClick('fabric', rating)}
								/>

								<StarRating
									param="stitching"
									label="Stitching"
									value={authParams.stitching}
									onChange={(rating) => handleStarClick('stitching', rating)}
								/>

								<StarRating
									param="labels"
									label="Labels"
									value={authParams.labels}
									onChange={(rating) => handleStarClick('labels', rating)}
								/>

								<StarRating
									param="condition"
									label="Condition"
									value={authParams.condition}
									onChange={(rating) => handleStarClick('condition', rating)}
								/>
							</div>
						</div>

						{/* Average Condition Card */}
						<div style={{
							backgroundColor: 'white',
							border: '1px solid #e5e7eb',
							borderRadius: '8px',
							padding: '20px',
							marginBottom: '16px',
							boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
							textAlign: 'center'
						}}>
							<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
								Average Condition
							</h3>
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
								<Star size={24} fill="#fbbf24" color="#fbbf24" />
								<span style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
									{getAverageRating()}
								</span>
								<span style={{ fontSize: '14px', color: '#6b7280' }}>/ 5.0</span>
							</div>
						</div>

						{/* Overall Remark Card */}
						<div style={{
							backgroundColor: 'white',
							border: '1px solid #e5e7eb',
							borderRadius: '8px',
							padding: '20px',
							boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
						}}>
							<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
								Overall Remark
							</h3>
							<textarea
								value={overallRemark}
								onChange={(e) => setOverallRemark(e.target.value)}
								rows={4}
								style={{
									width: '100%',
									border: '1px solid #d1d5db',
									borderRadius: '8px',
									padding: '12px',
									outline: 'none',
									fontSize: '14px',
									resize: 'vertical'
								}}
								placeholder="Add detailed remarks about the authentication assessment..."
							/>
							<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
								<label style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Overall Estimate (₹)</label>
								<input
									type="number"
									min="0"
									step="0.01"
									value={overallEstimate}
									onChange={(e) => setOverallEstimate(e.target.value)}
									placeholder="0.00"
									style={{
										width: '200px',
										border: '1px solid #d1d5db',
										borderRadius: '6px',
										padding: '8px 10px',
										fontSize: '14px'
									}}
								/>
							</div>
						</div>
					</div>
				)}

				{/* Status Actions */}
				<div style={{
					display: 'flex',
					gap: '12px',
					justifyContent: 'flex-end',
					paddingTop: '16px',
					borderTop: '1px solid #e5e7eb'
				}}>
				{(listing?.status === 0 || listing?.status === 2) && user?.role === 'authenticator' && (
					<>
						<button
							onClick={handleMarkAuthenticated}
							disabled={submittingAuthentication}
							className="btn"
							style={{
								backgroundColor: submittingAuthentication ? '#93c5fd' : '#3b82f6',
								color: 'white',
								border: '1px solid #3b82f6',
								cursor: submittingAuthentication ? 'not-allowed' : 'pointer',
								display: 'flex',
								alignItems: 'center',
								gap: '8px'
							}}
						>
							{submittingAuthentication ? (
								<>
									<Loader size={16} className="animate-spin" />
									Processing...
								</>
							) : (
								'Mark as Authenticated'
							)}
						</button>
						<button
							onClick={handleReject}
							disabled={submittingAuthentication}
							className="btn"
							style={{
								backgroundColor: submittingAuthentication ? '#fca5a5' : '#ef4444',
								color: 'white',
								border: '1px solid #ef4444',
								cursor: submittingAuthentication ? 'not-allowed' : 'pointer',
								display: 'flex',
								alignItems: 'center',
								gap: '8px'
							}}
						>
							{submittingAuthentication ? (
								<>
									<Loader size={16} className="animate-spin" />
									Processing...
								</>
							) : (
								'Reject'
							)}
						</button>
					</>
				)}
					{user?.role !== 'authenticator' && (
						<div style={{
							padding: '8px 16px',
							backgroundColor: '#f3f4f6',
							color: '#6b7280',
							borderRadius: '6px',
							fontSize: '14px',
							fontStyle: 'italic'
						}}>
							Only Authenticator role can update status
						</div>
					)}
				</div>
			</div>

			{/* Customer Service Remarks Section - Visible to Super Admin, Store Manager, Authenticator */}
			{(isSuperAdmin(user) || isStoreManager(user) || isAuthenticator(user)) && listing?.remarks && listing.remarks.length > 0 && (
				<div style={{
					backgroundColor: 'white',
					border: '1px solid #e5e7eb',
					borderRadius: '8px',
					padding: '24px',
					marginTop: '24px',
					boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
				}}>
					<div style={{
						display: 'flex',
						alignItems: 'center',
						gap: '12px',
						marginBottom: '20px',
						paddingBottom: '16px',
						borderBottom: '2px solid #e5e7eb'
					}}>
						<MessageSquare size={20} color="#3b82f6" />
						<h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
							Customer Service Remarks ({listing.remarks_count || listing.remarks.length})
						</h3>
					</div>
					
					<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
						{listing.remarks.map((remark) => (
							<div
								key={remark.id}
								style={{
									backgroundColor: '#f9fafb',
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									padding: '16px',
									borderLeft: '4px solid #3b82f6'
								}}
							>
								{remark.remark_type && (
									<div style={{ marginBottom: '8px' }}>
										<span style={{
											display: 'inline-block',
											padding: '4px 10px',
											backgroundColor: '#eff6ff',
											color: '#2563eb',
											borderRadius: '6px',
											fontSize: '12px',
											fontWeight: '600'
										}}>
											{remark.remark_type}
										</span>
									</div>
								)}
								<p style={{
									margin: '0 0 12px',
									color: '#374151',
									fontSize: '14px',
									lineHeight: '1.6',
									whiteSpace: 'pre-wrap'
								}}>
									{remark.remark}
								</p>
								<div style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									paddingTop: '12px',
									borderTop: '1px solid #e5e7eb',
									fontSize: '12px',
									color: '#6b7280'
								}}>
									<span>
										Added by: <strong>{remark.user?.name || 'Unknown'}</strong> ({remark.user?.email || 'N/A'})
									</span>
									<span>
										{new Date(remark.created_at).toLocaleString()}
									</span>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Return to Seller Modal */}
			{showReturnToSellerModal && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 1000,
					padding: '20px'
				}}>
					<div style={{
						backgroundColor: 'white',
						borderRadius: '12px',
						maxWidth: '520px',
						width: '100%',
						padding: '24px',
						boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
							<div style={{
								width: '48px',
								height: '48px',
								borderRadius: '50%',
								backgroundColor: '#ede9fe',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center'
							}}>
								<Truck size={24} style={{ color: '#7c3aed' }} />
							</div>
							<div>
								<h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
									Return to Seller
								</h3>
								<p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
									Provide courier details to notify the seller that their item is on the way back.
								</p>
							</div>
						</div>

						<div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
							<div>
								<label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
									Courier Name
								</label>
								<input
									type="text"
									value={returnToSellerForm.courierName}
									onChange={(e) => setReturnToSellerForm(prev => ({ ...prev, courierName: e.target.value }))}
									placeholder="e.g. BlueDart, DTDC"
									style={{
										width: '100%',
										padding: '10px 12px',
										borderRadius: '8px',
										border: '1px solid #d1d5db',
										fontSize: '14px'
									}}
								/>
							</div>

							<div>
								<label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
									AWB / Tracking Number
								</label>
								<input
									type="text"
									value={returnToSellerForm.awbNumber}
									onChange={(e) => setReturnToSellerForm(prev => ({ ...prev, awbNumber: e.target.value }))}
									placeholder="Enter tracking number"
									style={{
										width: '100%',
										padding: '10px 12px',
										borderRadius: '8px',
										border: '1px solid #d1d5db',
										fontSize: '14px'
									}}
								/>
							</div>

							{paidReturnPayment?.payment_id ? (
								<div style={{
									backgroundColor: '#f5f3ff',
									border: '1px solid #ddd6fe',
									borderRadius: '8px',
									padding: '12px 14px',
									fontSize: '12px',
									color: '#4c1d95'
								}}>
									<p style={{ margin: 0, fontWeight: '600' }}>Return Fee Reference</p>
									<p style={{ margin: '4px 0 0 0' }}>
										<strong>Payment ID:</strong> {paidReturnPayment.payment_id}
									</p>
									<p style={{ margin: '2px 0 0 0' }}>
										<strong>Amount:</strong> {formatCurrency(paidReturnPayment.amount)}
									</p>
								</div>
							) : null}
						</div>

						<div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
							<button
								onClick={() => setShowReturnToSellerModal(false)}
								disabled={returnSubmitting}
								className="btn"
								style={{
									backgroundColor: 'white',
									color: '#374151',
									border: '1px solid #d1d5db'
								}}
							>
								Cancel
							</button>
							<button
								onClick={submitReturnToSeller}
								disabled={returnSubmitting}
								className="btn"
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									minWidth: '140px',
									justifyContent: 'center',
									backgroundColor: '#7c3aed',
									color: 'white',
									border: '1px solid #7c3aed'
								}}
							>
								{returnSubmitting ? (
									<>
										<div style={{
											width: '16px',
											height: '16px',
											border: '2px solid #ffffff',
											borderTopColor: 'transparent',
											borderRadius: '50%',
											animation: 'spin 0.8s linear infinite'
										}} />
										Saving...
									</>
								) : (
									<>
										<Truck size={16} />
										Confirm Dispatch
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Send to Listing Confirmation Modal */}
			{showSendToListingModal && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 1000,
					padding: '20px'
				}}>
					<div style={{
						backgroundColor: 'white',
						borderRadius: '12px',
						maxWidth: '500px',
						width: '100%',
						padding: '24px',
						boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
							<div style={{
								width: '48px',
								height: '48px',
								borderRadius: '50%',
								backgroundColor: '#dcfce7',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center'
							}}>
								<CheckCircle size={24} style={{ color: '#16a34a' }} />
							</div>
							<h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
								Send to Listing
							</h3>
						</div>

						<div style={{ marginBottom: '20px' }}>
							<p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
								Are you sure you want to send this listing to live? This will make it visible to all users on the website.
							</p>
				{/* Branch selection for listing */}
				<div style={{ marginBottom: '12px' }}>
					<label style={{ display: 'block', fontSize: '14px', color: '#374151', marginBottom: '6px', fontWeight: 600 }}>
						Assign Store/Branch (required)
					</label>
					<select
						value={selectedBranchId || ''}
						onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : null)}
						className="form-select"
						style={{ width: '100%', height: '40px' }}
						disabled={branchesLoading}
					>
						<option value="">{branchesLoading ? 'Loading branches...' : 'Select a branch'}</option>
						{branches.map((b) => (
							<option key={b.id} value={b.id}>
								{b.branch_name || b.name || `Branch #${b.id}`}
							</option>
						))}
					</select>
				</div>
							{listing && (
								<div style={{
									backgroundColor: '#f9fafb',
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									padding: '12px',
									fontSize: '14px'
								}}>
									<div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
										Listing #{listing.listing_id}
									</div>
									<div style={{ color: '#6b7280' }}>
										{listing.designer_brand} • {listing.category}
									</div>
								</div>
							)}
						</div>

						<div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
							<button
								onClick={() => setShowSendToListingModal(false)}
								disabled={sendingToListing}
								className="btn"
								style={{
									backgroundColor: 'white',
									color: '#374151',
									border: '1px solid #d1d5db'
								}}
							>
								Cancel
							</button>
							<button
					onClick={handleSendToListing}
					disabled={sendingToListing || !selectedBranchId}
								className="btn btn-success"
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									minWidth: '120px',
									justifyContent: 'center'
								}}
							>
								{sendingToListing ? (
									<>
										<div style={{
											width: '16px',
											height: '16px',
											border: '2px solid #ffffff',
											borderTopColor: 'transparent',
											borderRadius: '50%',
											animation: 'spin 0.8s linear infinite'
										}} />
										Sending...
									</>
								) : (
									<>
										<CheckCircle size={16} />
										Confirm
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* De-list Confirmation Modal */}
			{showDelistModal && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 1000,
					padding: '20px'
				}}>
					<div style={{
						backgroundColor: 'white',
						borderRadius: '12px',
						maxWidth: '500px',
						width: '100%',
						padding: '24px',
						boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
							<div style={{
								width: '48px',
								height: '48px',
								borderRadius: '50%',
								backgroundColor: '#fee2e2',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center'
							}}>
								<AlertTriangle size={24} style={{ color: '#ef4444' }} />
							</div>
							<h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
								De-list Listing
							</h3>
						</div>

						<div style={{ marginBottom: '20px' }}>
							<p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
								Are you sure you want to de-list this item? This will remove it from the public website and mark it as de-listed.
							</p>
							{listing && (
								<div style={{
									backgroundColor: '#fef2f2',
									border: '1px solid #fecaca',
									borderRadius: '8px',
									padding: '12px',
									fontSize: '14px'
								}}>
									<div style={{ fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
										Listing #{listing.listing_id}
									</div>
									<div style={{ color: '#7f1d1d' }}>
										{listing.designer_brand} • {listing.category}
									</div>
								</div>
							)}
						</div>

						<div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
							<button
								onClick={() => setShowDelistModal(false)}
								disabled={delisting}
								className="btn"
								style={{
									backgroundColor: 'white',
									color: '#374151',
									border: '1px solid #d1d5db'
								}}
							>
								Cancel
							</button>
							<button
								onClick={handleDelist}
								disabled={delisting}
								className="btn"
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									minWidth: '120px',
									justifyContent: 'center',
									backgroundColor: '#ef4444',
									color: 'white',
									border: '1px solid #ef4444'
								}}
							>
								{delisting ? (
									<>
										<div style={{
											width: '16px',
											height: '16px',
											border: '2px solid #ffffff',
											borderTopColor: 'transparent',
											borderRadius: '50%',
											animation: 'spin 0.8s linear infinite'
										}} />
										De-listing...
									</>
								) : (
									<>
										<Trash2 size={16} />
										De-list
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Edit Listing Content Section */}
			<div style={{ 
				marginTop: '32px',
				backgroundColor: 'white',
				borderRadius: '8px',
				padding: '24px',
				boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',paddingBottom: '100px'
			}}>
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '20px'
				}}>
					<h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
						Listing Description
					</h3>
					{!editingContent ? (
						<button
							onClick={() => setEditingContent(true)}
							className="btn"
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								backgroundColor: '#3b82f6',
								color: 'white',
								border: '1px solid #3b82f6'
							}}
						>
							<Edit size={16} />
							Edit Description
						</button>
					) : (
						<div style={{ display: 'flex', gap: '12px' }}>
							<button
								onClick={() => {
									setEditingContent(false);
									setListingContent(listing?.listing_content || '');
								}}
								className="btn"
								style={{
									backgroundColor: 'white',
									color: '#374151',
									border: '1px solid #d1d5db'
								}}
								disabled={savingContent}
							>
								Cancel
							</button>
							<button
								onClick={async () => {
									setSavingContent(true);
									try {
										console.log('💾 Saving listing content:', listingContent);
										const response = await listingsService.update(listingId, {
											listing_content: listingContent
										});
										console.log('✅ Save response:', response);
										
										if (response.success) {
											showToast.success('Listing description updated successfully!');
											setEditingContent(false);
											setListing(response.data);
										} else {
											showToast.error('Failed to update listing description');
										}
									} catch (error) {
										console.error('Error updating listing description:', error);
										showToast.error('Error updating listing description');
									} finally {
										setSavingContent(false);
									}
								}}
								className="btn"
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									backgroundColor: '#10b981',
									color: 'white',
									border: '1px solid #10b981',
									minWidth: '120px',
									justifyContent: 'center'
								}}
								disabled={savingContent}
							>
								{savingContent ? (
									<>
										<Loader size={16} className="animate-spin" />
										Saving...
									</>
								) : (
									<>
										<CheckCircle size={16} />
										Save Changes
									</>
								)}
							</button>
						</div>
					)}
				</div>

				{editingContent ? (
					<div>
						<label style={{
							display: 'block',
							fontSize: '14px',
							fontWeight: '500',
							color: '#374151',
							marginBottom: '8px'
						}}>
							Description
						</label>
						<ReactQuill
							theme="snow"
							value={listingContent}
							onChange={setListingContent}
							placeholder="Write detailed content about the listing with headings, lists, images, etc..."
							style={{
								backgroundColor: 'white',
								minHeight: '500px',height: '400px'
							}}
							modules={{
								toolbar: [
									[{ 'header': [1, 2, 3, 4, 5, 6, false] }],
									[{ 'font': [] }],
									[{ 'size': ['small', false, 'large', 'huge'] }],
									['bold', 'italic', 'underline', 'strike'],
									[{ 'color': [] }, { 'background': [] }],
									[{ 'script': 'sub'}, { 'script': 'super' }],
									[{ 'list': 'ordered'}, { 'list': 'bullet' }],
									[{ 'indent': '-1'}, { 'indent': '+1' }],
									['blockquote', 'code-block'],
									['link', 'image', 'video'],
									[{ 'align': [] }],
									['clean']
								]
							}}
						/>
						<div style={{
							fontSize: '12px',
							color: '#6b7280',
							marginTop: '8px'
						}}>
							You can add detailed descriptions, multiple headings, lists, images, product history, care instructions, etc.
						</div>
					</div>
				) : (
					<div className="ql-snow" style={{ color: '#374151' }}>
						{listing?.listing_content ? (
							<>
								<div 
									className="ql-editor"
									dangerouslySetInnerHTML={{ __html: listing.listing_content }}
									style={{
										fontSize: '15px',
										lineHeight: '1.8',
										maxHeight: showFullDescription ? 'none' : '120px',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										display: '-webkit-box',
										WebkitLineClamp: showFullDescription ? 'none' : '5',
										WebkitBoxOrient: 'vertical'
									}}
								/>
								{listing.listing_content.length > 500 && (
									<button
										onClick={() => setShowFullDescription(!showFullDescription)}
										style={{
											marginTop: '12px',
											padding: '8px 16px',
											backgroundColor: 'transparent',
											border: '1px solid #d1d5db',
											borderRadius: '6px',
											color: '#6b7280',
											cursor: 'pointer',
											fontSize: '14px',
											transition: 'all 0.2s'
										}}
										onMouseEnter={(e) => {
											e.target.style.backgroundColor = '#f3f4f6';
											e.target.style.borderColor = '#9ca3af';
										}}
										onMouseLeave={(e) => {
											e.target.style.backgroundColor = 'transparent';
											e.target.style.borderColor = '#d1d5db';
										}}
									>
										{showFullDescription ? 'Show Less' : 'Show More'}
									</button>
								)}
							</>
						) : (
							<p style={{ fontStyle: 'italic', color: '#9ca3af' }}>No description added yet.</p>
						)}
					</div>
				)}
			</div>

			{/* Offers Section */}
			<div style={{ marginTop: '32px' }}>
				<OffersSection listingId={listingId} listing={listing} />
			</div>

			{/* Full Screen Image Modal */}
			{fullScreenImage && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.9)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 2000,
					padding: '20px'
				}}>
					<div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
						{/* Image Container with Redaction Overlay */}
						<div 
							style={{ 
								position: 'relative', 
								cursor: isRedactionMode ? 'crosshair' : 'default',
								userSelect: 'none'
							}}
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onMouseLeave={() => {
								if (isDrawing) {
									handleMouseUp();
								}
							}}
						>
							<img
								src={(() => {
									const currentImg = currentImageArray[currentImageIndex];
									let imageUrl;
									
									if (showOriginal) {
										// Show original (backup) image
										imageUrl = getImageUrl(currentImg?.backup_original_path) || 
											getImageUrl(currentImg?.backup_medium_path) || 
											getImageUrl(currentImg?.backup_thumbnail_path);
										console.log('🖼️ Showing ORIGINAL backup image:', imageUrl);
									} else {
										// Show current (redacted) image with cache busting
										const timestamp = new Date().getTime();
										const basePath = currentImg?.original_path || currentImg?.medium_path || currentImg?.url;
										imageUrl = basePath ? getImageUrl(basePath) + `?t=${timestamp}` : null;
										console.log('🖼️ Showing REDACTED image:', imageUrl);
									}
									
									return imageUrl || getImageUrl(fullScreenImage?.original_path) || getImageUrl(fullScreenImage?.url);
								})()}
								alt={currentImageArray[currentImageIndex]?.image_type || currentImageArray[currentImageIndex]?.alt || fullScreenImage?.alt || 'Image'}
								style={{
									maxWidth: '100%',
									maxHeight: '100%',
									objectFit: 'contain',
									display: 'block',
									pointerEvents: 'none'
								}}
								draggable={false}
								onLoad={() => console.log('✅ Image loaded successfully')}
								onError={(e) => console.error('❌ Image failed to load:', e.target.src)}
							/>

							{/* Redaction Overlays */}
							{(() => {
								const currentImage = currentImageArray[currentImageIndex];
								const imageId = currentImage?.id || currentImageIndex;
								const areas = redactionAreas[imageId] || [];
								
								return areas.map((area, idx) => (
									<div
										key={idx}
										style={{
											position: 'absolute',
											left: `${area.x}%`,
											top: `${area.y}%`,
											width: `${area.width}%`,
											height: `${area.height}%`,
											backgroundColor: 'white',
											border: '2px solid #ef4444',
											pointerEvents: 'none'
										}}
									/>
								));
							})()}

							{/* Current Drawing Rectangle */}
							{isDrawing && currentRedaction && (
								<div
									style={{
										position: 'absolute',
										left: `${Math.min(currentRedaction.startX, currentRedaction.endX)}%`,
										top: `${Math.min(currentRedaction.startY, currentRedaction.endY)}%`,
										width: `${Math.abs(currentRedaction.endX - currentRedaction.startX)}%`,
										height: `${Math.abs(currentRedaction.endY - currentRedaction.startY)}%`,
										border: '2px dashed #3b82f6',
										backgroundColor: 'rgba(255, 255, 255, 0.3)',
										pointerEvents: 'none'
									}}
								/>
							)}
						</div>

						{/* Navigation Arrows */}
						{currentImageArray.length > 1 && (
							<>
								<button
									onClick={() => navigateImage('prev')}
									style={{
										position: 'absolute',
										left: '20px',
										top: '50%',
										transform: 'translateY(-50%)',
										background: 'rgba(0, 0, 0, 0.5)',
										color: 'white',
										border: 'none',
										borderRadius: '50%',
										width: '50px',
										height: '50px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										cursor: 'pointer'
									}}
								>
									<ChevronLeft size={24} />
								</button>
								<button
									onClick={() => navigateImage('next')}
									style={{
										position: 'absolute',
										right: '20px',
										top: '50%',
										transform: 'translateY(-50%)',
										background: 'rgba(0, 0, 0, 0.5)',
										color: 'white',
										border: 'none',
										borderRadius: '50%',
										width: '50px',
										height: '50px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										cursor: 'pointer'
									}}
								>
									<ChevronRight size={24} />
								</button>
							</>
						)}

						{/* Redaction Controls Toolbar */}
						<div style={{
							position: 'absolute',
							top: '20px',
							left: '50%',
							transform: 'translateX(-50%)',
							display: 'flex',
							gap: '8px',
							backgroundColor: 'rgba(0, 0, 0, 0.7)',
							padding: '8px',
							borderRadius: '8px'
						}}>
							{/* View Original Button - Only show if image has backups */}
							{currentImageArray[currentImageIndex]?.backup_original_path && (
								<button
									onClick={() => setShowOriginal(!showOriginal)}
									style={{
										backgroundColor: showOriginal ? '#10b981' : 'rgba(255, 255, 255, 0.2)',
										color: 'white',
										border: 'none',
										borderRadius: '6px',
										padding: '8px 16px',
										cursor: 'pointer',
										fontSize: '14px',
										fontWeight: '500',
										display: 'flex',
										alignItems: 'center',
										gap: '6px'
									}}
									title={showOriginal ? 'Show Redacted Version' : 'Show Original (Before Redaction)'}
								>
									<Eye size={16} />
									{showOriginal ? 'Redacted' : 'Original'}
								</button>
							)}
							
							<button
								onClick={() => setIsRedactionMode(!isRedactionMode)}
								style={{
									backgroundColor: isRedactionMode ? '#3b82f6' : 'rgba(255, 255, 255, 0.2)',
									color: 'white',
									border: 'none',
									borderRadius: '6px',
									padding: '8px 16px',
									cursor: 'pointer',
									fontSize: '14px',
									fontWeight: '500',
									display: 'flex',
									alignItems: 'center',
									gap: '6px'
								}}
								title={isRedactionMode ? 'Exit Redaction Mode' : 'Enter Redaction Mode'}
							>
								<Edit size={16} />
								{isRedactionMode ? 'Exit Redaction' : 'Redact Info'}
							</button>
							
							{isRedactionMode && (
								<>
									<button
										onClick={undoLastRedaction}
										style={{
											backgroundColor: 'rgba(255, 255, 255, 0.2)',
											color: 'white',
											border: 'none',
											borderRadius: '6px',
											padding: '8px 16px',
											cursor: 'pointer',
											fontSize: '14px',
											fontWeight: '500'
										}}
										title="Undo Last Redaction"
									>
										Undo
									</button>
									<button
										onClick={clearAllRedactions}
										style={{
											backgroundColor: 'rgba(239, 68, 68, 0.8)',
											color: 'white',
											border: 'none',
											borderRadius: '6px',
											padding: '8px 16px',
											cursor: 'pointer',
											fontSize: '14px',
											fontWeight: '500'
										}}
										title="Clear All Redactions"
									>
										Clear All
									</button>
									<button
										onClick={saveRedactions}
										style={{
											backgroundColor: '#10b981',
											color: 'white',
											border: 'none',
											borderRadius: '6px',
											padding: '8px 16px',
											cursor: 'pointer',
											fontSize: '14px',
											fontWeight: '500',
											display: 'flex',
											alignItems: 'center',
											gap: '6px'
										}}
										title="Save Redactions"
									>
										<CheckCircle size={16} />
										Save
									</button>
								</>
							)}
						</div>

						{/* Close Button */}
						<button
							onClick={closeImageModal}
							style={{
								position: 'absolute',
								top: '20px',
								right: '20px',
								background: 'rgba(0, 0, 0, 0.5)',
								color: 'white',
								border: 'none',
								borderRadius: '50%',
								width: '40px',
								height: '40px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								cursor: 'pointer'
							}}
						>
							<X size={20} />
						</button>

						{/* Redaction Mode Instructions */}
						{isRedactionMode && (
							<div style={{
								position: 'absolute',
								top: '80px',
								left: '50%',
								transform: 'translateX(-50%)',
								backgroundColor: 'rgba(59, 130, 246, 0.9)',
								color: 'white',
								padding: '12px 20px',
								borderRadius: '8px',
								fontSize: '14px',
								fontWeight: '500',
								textAlign: 'center',
								maxWidth: '400px'
							}}>
								<div style={{ marginBottom: '4px', fontWeight: '600' }}>Redaction Mode Active</div>
								<div style={{ fontSize: '12px' }}>Click and drag to select areas to hide (name, address, phone, etc.)</div>
							</div>
						)}

						{/* Original Version Indicator */}
						{showOriginal && (
							<div style={{
								position: 'absolute',
								top: '80px',
								left: '50%',
								transform: 'translateX(-50%)',
								backgroundColor: 'rgba(16, 185, 129, 0.9)',
								color: 'white',
								padding: '12px 20px',
								borderRadius: '8px',
								fontSize: '14px',
								fontWeight: '600',
								textAlign: 'center',
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
							}}>
								<Eye size={16} />
								Viewing Original (Backup) - Before Redaction
							</div>
						)}

						{/* Image Info */}
						<div style={{
							position: 'absolute',
							bottom: '20px',
							left: '50%',
							transform: 'translateX(-50%)',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '8px'
						}}>
							{/* Redaction Count */}
							{(() => {
								const currentImage = currentImageArray[currentImageIndex];
								const imageId = currentImage?.id || currentImageIndex;
								const areas = redactionAreas[imageId] || [];
								
								return areas.length > 0 && (
									<div style={{
										background: 'rgba(239, 68, 68, 0.9)',
										color: 'white',
										padding: '6px 12px',
										borderRadius: '12px',
										fontSize: '12px',
										fontWeight: '600'
									}}>
										{areas.length} Redaction{areas.length > 1 ? 's' : ''} Applied
									</div>
								);
							})()}

							{/* Permanently Redacted Badge */}
							{redactedImages.has(currentImageArray[currentImageIndex]?.id) && (
								<div style={{
									background: 'rgba(239, 68, 68, 0.95)',
									color: 'white',
									padding: '8px 16px',
									borderRadius: '12px',
									fontSize: '13px',
									fontWeight: '600',
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
								}}>
									<CheckCircle size={14} />
									Redacted (Backup Saved)
								</div>
							)}

							{currentImageArray[currentImageIndex]?.image_type && (
								<div style={{
									background: 'rgba(0, 0, 0, 0.7)',
									color: 'white',
									padding: '6px 12px',
									borderRadius: '12px',
									fontSize: '12px',
									textTransform: 'capitalize'
								}}>
									{currentImageArray[currentImageIndex].image_type.replace(/_/g, ' ')}
								</div>
							)}
							{currentImageArray.length > 1 && (
								<div style={{
									background: 'rgba(0, 0, 0, 0.5)',
									color: 'white',
									padding: '8px 16px',
									borderRadius: '20px',
									fontSize: '14px'
								}}>
									{currentImageIndex + 1} / {currentImageArray.length}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ListingDetails;